import { motion } from 'framer-motion';
import { Play, Plus, X } from 'lucide-react';
import { useState } from 'react';

const WEBHOOK_URL = 'https://n8n.idgate.in/webhook/idgate-leads';

const DEFAULT_KEYWORDS = [
  'boutique hotel', 'luxury hotel', 'resort', 'gym',
  'fitness studio', 'dental clinic', 'private hospital', 'yoga studio',
];

export default function Leads() {
  const [keywords, setKeywords] = useState(DEFAULT_KEYWORDS);
  const [kwInput, setKwInput] = useState('');
  const [city, setCity] = useState('Madurai');
  const [area, setArea] = useState('');
  const [road, setRoad] = useState('');
  const [maxResults, setMaxResults] = useState(200);
  const [status, setStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const addKeyword = raw => {
    const kws = raw.split(',').map(k => k.trim()).filter(Boolean);
    setKeywords(prev => [...new Set([...prev, ...kws])]);
    setKwInput('');
  };

  const removeKeyword = kw => setKeywords(prev => prev.filter(k => k !== kw));

  const handleKwKeyDown = e => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (kwInput.trim()) addKeyword(kwInput);
    }
  };

  const handleGenerate = async () => {
    if (keywords.length === 0) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          icp_keywords: keywords,
          city: city.trim(),
          area: area.trim(),
          road: road.trim(),
          max_results: Number(maxResults),
        }),
      });
      if (res.ok) {
        setStatus('success');
      } else {
        const text = await res.text();
        setErrorMsg(text || `HTTP ${res.status}`);
        setStatus('error');
      }
    } catch (e) {
      setErrorMsg(e.message);
      setStatus('error');
    }
  };

  const cityCount = city.trim() ? 1 : 3;
  const totalQueries = keywords.length * cityCount;

  return (
    <div className="min-h-screen bg-surface-page py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-surface-card rounded-2xl shadow-lg p-8"
        >
          <h1 className="text-2xl font-bold text-surface-ink mb-1">Lead Generation</h1>
          <p className="text-surface-body text-sm mb-8">
            Triggers the n8n workflow — scrapes Google Places, enriches via Apollo, pushes to HubSpot &amp; Sheets.
          </p>

          {/* Keywords */}
          <div className="mb-6">
            <label htmlFor="kw-input" className="block text-sm font-semibold text-surface-ink mb-2">ICP Keywords</label>
            <div className="flex flex-wrap gap-2 mb-3 min-h-[2rem]">
              {keywords.map(kw => (
                <span key={kw} className="flex items-center gap-1 bg-primary-100 text-primary-700 text-sm px-3 py-1 rounded-full">
                  {kw}
                  <button onClick={() => removeKeyword(kw)} className="hover:text-primary-900 ml-0.5">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                id="kw-input"
                type="text"
                value={kwInput}
                onChange={e => setKwInput(e.target.value)}
                onKeyDown={handleKwKeyDown}
                placeholder="Add keyword and press Enter"
                className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button
                onClick={() => { if (kwInput.trim()) addKeyword(kwInput); }}
                className="px-3 py-2 bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 transition-colors"
              >
                <Plus size={16} />
              </button>
            </div>
            <p className="text-xs text-surface-body mt-1">Press Enter or comma to add multiple</p>
          </div>

          {/* City / Area / Road */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div>
              <label htmlFor="city" className="block text-sm font-semibold text-surface-ink mb-1">City</label>
              <input
                id="city"
                type="text"
                value={city}
                onChange={e => setCity(e.target.value)}
                placeholder="Leave blank for all"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-xs text-surface-body mt-1">Blank → Madurai, Bangalore, Chennai</p>
            </div>
            <div>
              <label htmlFor="area" className="block text-sm font-semibold text-surface-ink mb-1">Area</label>
              <input
                id="area"
                type="text"
                value={area}
                onChange={e => setArea(e.target.value)}
                placeholder="e.g. Koramangala"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor="road" className="block text-sm font-semibold text-surface-ink mb-1">Road</label>
              <input
                id="road"
                type="text"
                value={road}
                onChange={e => setRoad(e.target.value)}
                placeholder="e.g. Outer Ring Road"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Max Results */}
          <div className="mb-8">
            <label htmlFor="max-results" className="block text-sm font-semibold text-surface-ink mb-1">
              Max Results{' '}
              <span className="text-surface-body font-normal">(per keyword per city)</span>
            </label>
            <input
              id="max-results"
              type="number"
              value={maxResults}
              min={1}
              max={200}
              onChange={e => setMaxResults(e.target.value)}
              className="w-28 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Summary + Trigger */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-surface-body">
              {keywords.length} keyword{keywords.length !== 1 ? 's' : ''} ×{' '}
              {city.trim() ? '1 city' : '3 cities'} ={' '}
              <strong className="text-surface-ink">{totalQueries} searches</strong>
            </p>
            <button
              onClick={handleGenerate}
              disabled={status === 'loading' || keywords.length === 0}
              className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Play size={15} />
              {status === 'loading' ? 'Triggering…' : 'Generate Leads'}
            </button>
          </div>

          {status === 'success' && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 bg-green-50 border border-green-200 text-green-800 rounded-xl px-4 py-3 text-sm"
            >
              ✅ Workflow triggered — check the <strong>Executions</strong> tab in n8n or Slack for results.
            </motion.div>
          )}

          {status === 'error' && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 bg-red-50 border border-red-200 text-red-800 rounded-xl px-4 py-3 text-sm"
            >
              ❌ {errorMsg}
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
